import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useTurnstileGate } from "../hooks/useTurnstileGate";

interface TurnstileGateProps {
  children: ReactNode;
  /** Shown while verifying. Reuses the existing map placeholder so there is no new visual state. */
  placeholder: ReactNode;
}

/**
 * Defers Google Maps loading until a Turnstile challenge has been verified
 * server-side, so headless scrapers do not trigger billed map loads.
 *
 * Fails open on every infrastructure problem (blocked script, Cloudflare
 * outage, backend unreachable) - only an authoritative rejection blocks.
 */
export function TurnstileGate({ children, placeholder }: TurnstileGateProps) {
  const { status, slow, containerRef, retry } = useTurnstileGate();
  const { t } = useTranslation();

  return (
    <>
      {status === "allowed" ? (
        children
      ) : status === "blocked" ? (
        <div className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
          <div className="text-center px-6">
            <p className="text-gray-700 dark:text-gray-200 mb-3">
              {t("mapGate.blocked")}
            </p>
            <button
              type="button"
              onClick={retry}
              className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              {t("mapGate.retry")}
            </button>
          </div>
        </div>
      ) : (
        <>
          {placeholder}
          {/* The map is intentionally withheld until the challenge is solved,
              so tell a stalled visitor what is happening rather than leaving
              them staring at a loading placeholder. */}
          {slow && (
            <div className="absolute inset-x-0 bottom-24 z-20 flex justify-center px-4 pointer-events-none">
              <p className="text-sm text-center text-gray-700 dark:text-gray-200 bg-white/90 dark:bg-gray-800/90 rounded px-3 py-2 shadow">
                {t("mapGate.slow")}
              </p>
            </div>
          )}
        </>
      )}

      {/*
        The widget host must exist from first mount so turnstile.render() has a
        target. appearance="interaction-only" keeps it invisible unless
        Cloudflare escalates to a checkbox.
      */}
      <div
        ref={containerRef}
        className="absolute inset-x-0 bottom-6 z-20 flex justify-center pointer-events-none [&>*]:pointer-events-auto"
      />
    </>
  );
}
